import { DisabledPrompt, useRoute, useWorkflowStep } from "@bcm-dev/bcms-vite";
import UIRadioGroup, { UIRadio } from "components/custom-ui/UIRadioGroup";
import { CenterContentContainer, ContentHeaderView, PageContainer, PageContentView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView } from "components/shared";
import { PrimaryButton, SecondaryButton, UICheckbox, UITextInput } from "components/ui";
import { getDefault, getWorkflowSteps } from "components/workflows/MobileNicknamesWorkflow";
import React from "react";

import "./styles.css";
import "./mobile.css";


/**
 * @function MobileNicknamesPage
 * @return {React.Component}
 * @exported true
 * @description The main component for MobileNicknamesPage
 */
export default function MobileNicknamesPage(props) {
  const navigate = useRoute();
  const workflowStep = useWorkflowStep(getDefault, getWorkflowSteps);
  const mobileNicknames = workflowStep.read("mobileNicknames");

  // Only 1 mobile nickname can be selected in the "Make Primary" column.
  const selectedPrimaryMobileNickname = mobileNicknames.filter(nickname => {
    return nickname.primary === true;
  }, [])[0];
  const hasSelectedNickname = selectedPrimaryMobileNickname === undefined ? false : true;

  // Set changes made on the "Enable" column.
  const onEnableChange = obj => value => {
    let selectedMobileNickname = obj.mobileNickname;
    let _mobileNicknames = mobileNicknames;
    let prop = obj.prop;
    // Update the selected mobile nickname using the property and values passed.
    _mobileNicknames = _mobileNicknames.reduce((acc, nickname) => {
      nickname[prop] = nickname.id === selectedMobileNickname.id ? value : nickname[prop];
      acc.push(nickname);
      return acc;
    }, []);
    
    // Update the workflow state.
    workflowStep.update(["mobileNicknames"], _mobileNicknames);
  };
  
  // Set the component rows created from the array of mobileNicknames.
  const rows = mobileNicknames.reduce((acc, nickname) => {
    acc.push(
      <MobileNicknameTableRow key={ nickname.id } mobileNickname={ nickname } onEnableChange={ onEnableChange } />
    );
    return acc;
  }, []);

  // Set changes made on the "Make Primary" column.
  const onPrimaryChange = value => {
    const mobileNicknameID = value;
    let _mobileNicknames = mobileNicknames;
    _mobileNicknames = _mobileNicknames.reduce((acc, nickname) => {
      nickname.primary = nickname.id.toString() === mobileNicknameID ? true : false;
      acc.push(nickname);
      return acc;
    }, []);

    // Update the workflow state.
    workflowStep.update(["mobileNicknames"], _mobileNicknames);
  }

  return (
    <PageContainer className="mobile-nicknames-page">
      <PageContentView>
        <CenterContentContainer>
          <ContentHeaderView text="Mobile Nicknames" />
          <div className="content-container">
            <p>Create mobile nicknames to access your accounts with Text Banking. These are separate from the nicknames you created for Online Banking.</p>
          </div>
          <div className="content-container">
            <p>For example, to get the available balance on an account, send the text “BAL SAVA” to 72933</p>
          </div>
          <UIRadioGroup value={ hasSelectedNickname ? selectedPrimaryMobileNickname.id.toString() : "" } onChange={ onPrimaryChange }>
            <TableView>
              <TableColumnContainer>
                <TableColumnView className="text-left enable-column" text="Enable" />
                <TableColumnView className="text-left account-column" text="Account" />
                <TableColumnView className="text-left nickname-column" text="Nickname" />
                <TableColumnView className="text-left primary-column" text="Primary" />
              </TableColumnContainer>
                <TableBody>
                  { rows }
                </TableBody>
            </TableView>
          </UIRadioGroup>
          <div className="flex-container flex-horizontal align-center button-container">
            <SecondaryButton text="Cancel" onClick={() => {navigate("/settingsMenu")}} />
            <DisabledPrompt>
              <PrimaryButton text="Submit" />
            </DisabledPrompt>
          </div>
        </CenterContentContainer>
      </PageContentView>
    </PageContainer>
  )
}

/**
 * @function MobileNicknameTableRow
 * @param {STRING} accountNumber - Sets the number of the account. 
 * @param {STRING} accountName - Sets the name of the account. 
 * @return {React.Component}
 * @exported false
 * @description The main component for MobileNicknameTableRow
 */
function MobileNicknameTableRow(props) {
  const mobileNickname = props.mobileNickname ?? {};
  const account = mobileNickname.account ?? {};

  return (
    <TableItemContainer>
      <TableItemView className="ui-checkbox-item">
        <UICheckbox label="" value={ mobileNickname.enabled } 
        onChange={ props.onEnableChange({ mobileNickname, prop: "enabled" }) } />
      </TableItemView>
      <TableItemTextView className="account-info-item" text={ `${account.number} ${account.nickname}` } />
      <TableItemView className="ui-text-input-item">
        <UITextInput disabled={ true } placeholder={ mobileNickname.nickname } />
      </TableItemView>
      <TableItemView className="ui-radio-item">
        <UIRadio name="primary" value={ mobileNickname.id.toString() } />
      </TableItemView>
    </TableItemContainer>
  )
}